import { type ExchangeResult, type ExchangeSummary, type OtpChallenge, exchangeLinkToken, getCaptureState, isOtpChallenge } from '@/api/capture';
import { SvApiError } from '@/api/errors';
import { carryDeviceChoiceToCase, chooseLocale } from '@/i18n/persistLocale';
import { hasStoredLocale, offerableLocales, storedLocale, useT } from '@/i18n/runtime';
import { useCaptureStore } from '@/store/captureStore';
import { tenantFrom, tenantFromSummary } from '@/theme/tenant';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConsentStep } from './ConsentStep';
import { LanguageChooser } from './LanguageChooser';
import { OtpStep, otpFailureOf } from './OtpStep';
import { AppBar } from './components/AppBar';
import { Button } from './components/Button';
import { ConcernPanel } from './components/ConcernPanel';
import { Icon } from './components/Icon';
import { CentredNotice, Shell } from './components/Shell';
import { HelpStepMarker } from './help/HelpContext';
import { captureListLines } from './captureList';
import { premisesKindText } from './premisesKind';

/**
 * The link's purpose in the customer's words (FSD-18 D7). The be sends the raw `sv_capture_link.purpose` word
 * (`CAPTURE` · `RECAPTURE` · `REVISIT` · `REVERIFY` · `FIELD_VISIT` — every real wire dump says `CAPTURE`); an
 * enum word this build does not know reads as the generic line, never the raw value. Text that is already a sentence
 * (the mock adapter's purpose lines) is shown as given.
 */
export function purposeLabel(t: (key: string) => string, purpose: string | null | undefined): string {
  const word = (purpose ?? '').trim();
  if (word !== '' && !/^[A-Z][A-Z0-9_]*$/.test(word)) return word;
  const key = `sv.door.purpose.${word}`;
  const label = t(key);
  return label === key ? t('sv.door.purpose.other') : label;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'otp'; challenge: OtpChallenge }
  | { kind: 'otp-locked' }
  | { kind: 'otp-unavailable' }
  | { kind: 'door'; summary: ExchangeSummary; consentDone: boolean; liveJoinable: boolean; liveConsentRequired: boolean }
  /** SPEC_CAPTURE_UX_R2 §2: the language chooser — first after the door, only when no choice is known yet. */
  | { kind: 'language'; summary: ExchangeSummary; consentDone: boolean }
  /** `next`: where a recorded consent leads — the capture flow, or (SV-5-2 re-consent) straight to the live step. */
  | { kind: 'consent'; summary: ExchangeSummary; next: '/capture' | '/live' }
  | { kind: 'stopped'; summary: ExchangeSummary }
  | { kind: 'expired' }
  | { kind: 'closed' }
  | { kind: 'unreachable' }
  | { kind: 'invalid' };

/**
 * `/v/:linkToken` — the anti-impersonation front door (FSD §2.6 J0) + the consent ceremony, with the OTP
 * front door (SPEC-15) in front of it when the tenant requires one.
 *
 * The link token buys ONE exchange (POST /exchange) for an in-memory, case-scoped bearer; the
 * token itself never appears in a header, storage or log after that. When the tenant's `sv.link_otp` is
 * on, the exchange answers a challenge and NO bearer: the OTP screen runs, `POST /otp/verify` returns the
 * same exchange payload, and the flow continues exactly as before. A typed refusal (410 / 409 / locked /
 * channel unavailable) renders the honest state — no fabricated session, no retry loop. The door renders
 * on EVERY visit, including resumes: the representative must always see who is asking, for what, and the
 * sentence that no one will ask for OTPs or passwords outside this page.
 */
export function FrontDoor(): JSX.Element {
  const { linkToken } = useParams();
  const navigate = useNavigate();
  const { t, locale, setLocale } = useT();
  const setLinkToken = useCaptureStore((s) => s.setLinkToken);
  const setExchange = useCaptureStore((s) => s.setExchange);
  const setCaptureState = useCaptureStore((s) => s.setCaptureState);
  const setFrontDoorPackLocales = useCaptureStore((s) => s.setFrontDoorPackLocales);
  const setFrontDoorLocale = useCaptureStore((s) => s.setFrontDoorLocale);
  // The runtime's setter changes identity whenever the offered set does (the challenge's packLocales arriving); held in
  // a ref so the exchange effect never re-runs — a re-run would re-exchange the link and send a second code.
  const setLocaleRef = useRef(setLocale);
  setLocaleRef.current = setLocale;
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [reload, setReload] = useState(0);

  // After the bearer exists (from the exchange OR the OTP verify): record the summary, read server truth, open the door.
  const afterExchange = useCallback(
    async (ex: ExchangeResult, isActive: () => boolean): Promise<void> => {
      setExchange(ex.summary, ex.expiresAt);
      // VC-12 r5: a language tapped on the code screen (no bearer then) lands on the case now that the grant exists —
      // the device already remembers it, so the chooser is skipped; the case learns it here (best-effort, as ever).
      const tapped = useCaptureStore.getState().frontDoorLocale;
      if (tapped) {
        setFrontDoorLocale(null);
        if (tapped !== ex.summary.customerLocale && offerableLocales(ex.summary.packLocales).includes(tapped)) {
          void chooseLocale(tapped, (next) => setLocaleRef.current(next));
        }
      }
      // Whether consent is already on record is server truth (GET /state.status ≠ INVITED).
      let consentDone = false;
      // SV-5-2: a case waiting for its live leg re-opens THIS link to join the call — the door still renders
      // (the representative always sees who is asking), and its continue leads to /live, not to capture.
      let liveJoinable = false;
      // SV-5-2 second round: the be says whether the three live purposes are still unconsented — then "Join now"
      // runs the ceremony first (the same v2 summary, with its liveLeg rows) and lands on /live after it.
      let liveConsentRequired = false;
      try {
        const st = await getCaptureState();
        if (!isActive()) return;
        setCaptureState(st);
        consentDone = st.status !== 'INVITED';
        liveJoinable = st.liveLeg?.joinable === true;
        liveConsentRequired = liveJoinable && st.liveLeg?.consentRequired === true;
      } catch {
        /* an unreadable state is not a refusal — consent runs again; the server dedups */
      }
      if (!isActive()) return;
      setPhase({ kind: 'door', summary: ex.summary, consentDone, liveJoinable, liveConsentRequired });
    },
    [setExchange, setCaptureState, setFrontDoorLocale],
  );

  useEffect(() => {
    if (!linkToken) {
      setPhase({ kind: 'invalid' });
      return;
    }
    let active = true;
    const isActive = (): boolean => active;
    setPhase({ kind: 'loading' });
    setLinkToken(linkToken);
    (async () => {
      try {
        const out = await exchangeLinkToken(linkToken);
        if (!active) return;
        if (isOtpChallenge(out)) {
          // VC-12 r5: the pack's languages reach the locale runtime before any summary exists (absent on India)
          setFrontDoorPackLocales(Array.isArray(out.packLocales) && out.packLocales.length > 0 ? out.packLocales : null);
          setPhase({ kind: 'otp', challenge: out });
          return;
        }
        await afterExchange(out, isActive);
      } catch (e) {
        if (!active) return;
        if (e instanceof SvApiError && e.code === 'SV_LINK_EXPIRED') setPhase({ kind: 'expired' });
        else if (e instanceof SvApiError && e.code === 'SV_CASE_NOT_CAPTURABLE') setPhase({ kind: 'closed' });
        else if (e instanceof SvApiError && otpFailureOf(e).kind === 'channelUnavailable') setPhase({ kind: 'otp-unavailable' });
        else if (e instanceof SvApiError && otpFailureOf(e).kind === 'locked') setPhase({ kind: 'otp-locked' });
        else if (e instanceof SvApiError && e.status < 500) setPhase({ kind: 'invalid' });
        else setPhase({ kind: 'unreachable' });
      }
    })();
    return () => {
      active = false;
    };
  }, [linkToken, reload, setLinkToken, afterExchange, setFrontDoorPackLocales]);

  if (phase.kind === 'loading') {
    return (
      <Shell tenant={null}>
        <div className="app-view center">
          <div className="app-preparing" role="status">
            {t('sv.entry.preparing')}
          </div>
        </div>
      </Shell>
    );
  }
  if (phase.kind === 'expired') return <Shell tenant={null}><CentredNotice title={t('sv.entry.expired.title')} body={t('sv.entry.expired.body')} /></Shell>;
  if (phase.kind === 'closed') return <Shell tenant={null}><CentredNotice title={t('sv.entry.closed.title')} body={t('sv.entry.closed.body')} /></Shell>;
  if (phase.kind === 'invalid') return <Shell tenant={null}><CentredNotice title={t('sv.entry.notFound.title')} body={t('sv.entry.notFound.body')} /></Shell>;
  if (phase.kind === 'unreachable') {
    return (
      <Shell tenant={null}>
        <CentredNotice
          title={t('sv.entry.unreachable.title')}
          body={t('sv.entry.unreachable.body')}
          action={<Button onClick={() => setReload((n) => n + 1)}>{t('sv.action.tryAgain')}</Button>}
        />
      </Shell>
    );
  }
  if (phase.kind === 'otp-locked') return <Shell tenant={null}><OtpStep challenge={null} tenant={null} blocked="locked" onVerified={() => undefined} /></Shell>;
  if (phase.kind === 'otp-unavailable') {
    return <Shell tenant={null}><OtpStep challenge={null} tenant={null} blocked="channelUnavailable" onVerified={() => undefined} /></Shell>;
  }
  if (phase.kind === 'otp') {
    // The tenant is not proven until the code is; the challenge carries no name/branding on today's wire.
    return (
      <Shell tenant={null}>
        <OtpStep
          challenge={phase.challenge}
          tenant={null}
          onVerified={(ex) => {
            setPhase({ kind: 'loading' });
            void afterExchange(ex, () => true);
          }}
        />
      </Shell>
    );
  }

  const tenant = tenantFromSummary(phase.summary) ?? tenantFrom('');

  if (phase.kind === 'stopped') {
    return <Shell tenant={tenant}><CentredNotice title={t('sv.door.concern.stopped.title')} body={t('sv.door.concern.stopped.body')} /></Shell>;
  }

  if (phase.kind === 'consent') {
    return (
      <Shell tenant={tenant}>
        <ConsentStep tenant={tenant} summary={phase.summary} onDone={() => navigate(phase.next, { replace: true })} />
      </Shell>
    );
  }

  if (phase.kind === 'language') {
    const { summary, consentDone } = phase;
    return (
      <Shell tenant={tenant}>
        <LanguageChooser tenant={tenant} summary={summary} onChosen={() => (consentDone ? navigate('/capture', { replace: true }) : setPhase({ kind: 'consent', summary, next: '/capture' }))} />
      </Shell>
    );
  }

  const { summary, consentDone, liveJoinable, liveConsentRequired } = phase;
  const never = summary.tenantName ? t('sv.door.never', { tenantName: summary.tenantName }) : t('sv.door.neverGeneric');
  // The chooser is asked once: skipped when this device remembers a choice or the case already carries one (§2 acceptance).
  // VC-12: both read against THIS case's offer — Arabic remembered from a Gulf case does not skip an India case's chooser.
  const needsChooser = !hasStoredLocale(summary.packLocales) && !(summary.customerLocale && offerableLocales(summary.packLocales).includes(summary.customerLocale));
  const continueFromDoor = (): void => {
    if (!needsChooser) void carryDeviceChoiceToCase(summary.customerLocale, storedLocale(summary.packLocales), setLocale);
    if (needsChooser) setPhase({ kind: 'language', summary, consentDone });
    else if (consentDone) navigate('/capture', { replace: true });
    else setPhase({ kind: 'consent', summary, next: '/capture' });
  };
  return (
    <Shell tenant={tenant}>
      <div className="app-view">
        <HelpStepMarker screen="door" tenantName={summary.tenantName} />
        <AppBar tenant={tenant} subtitle={t('sv.subtitle')} />
        <div className="stack">
          <div>
            <div className="h-title sm">{t('sv.door.title')}</div>
            <p className="sub-sm" style={{ marginTop: 6 }}>
              {t('sv.door.sub')}
            </p>
          </div>

          <div className="card notice-summary" data-testid="door-summary">
            <div className="purpose">
              <span className="pic"><Icon name="shield" size={16} /></span>
              <div>
                <div className="pt">{t('sv.door.client')}</div>
                <div className="pd" data-testid="door-client">{summary.tenantName}</div>
              </div>
            </div>
            <div className="purpose">
              <span className="pic"><Icon name="info" size={16} /></span>
              <div>
                <div className="pt">{t('sv.door.purpose')}</div>
                <div className="pd" data-testid="door-purpose">{purposeLabel(t, summary.purpose)}</div>
              </div>
            </div>
            <div className="purpose">
              <span className="pic"><Icon name="doc" size={16} /></span>
              <div>
                <div className="pt">{t('sv.door.reference')}</div>
                <div className="pd mono" data-testid="door-reference">{summary.clientRef}</div>
              </div>
            </div>
            <div className="purpose">
              <span className="pic"><Icon name="mappin" size={16} /></span>
              <div>
                <div className="pt">{t('sv.door.premises')}</div>
                <div className="pd" data-testid="door-premises-kind">{premisesKindText(summary, t, locale)}</div>
              </div>
            </div>
          </div>

          {liveJoinable ? (
            <div className="card pad" data-testid="door-live-card">
              <div className="pt">{t('sv.live.card.title')}</div>
              <p className="pd" style={{ marginTop: 4 }}>{t('sv.live.card.body')}</p>
            </div>
          ) : null}

          <div className="card pad">
            <div className="pt" style={{ marginBottom: 6 }}>{t('sv.door.captureList')}</div>
            <ul className="sv-capture-list" data-testid="door-capture-list">
              {captureListLines(summary, locale).map((item, i) => (
                <li key={`${i}-${item}`} dir="auto">{item}</li>
              ))}
            </ul>
            <p className="sub-sm" style={{ marginTop: 8 }}>{t('sv.door.time')}</p>
          </div>

          <p className="sv-never" role="note" data-testid="door-never">
            <Icon name="lock" size={14} /> {never}
          </p>

          {tenant.branding.cobrand ? <div className="cobrand" data-testid="door-cobrand">{tenant.branding.cobrand}</div> : null}

          <ConcernPanel tenantName={summary.tenantName} onStop={() => setPhase({ kind: 'stopped', summary })} />
        </div>

        <div className="spacer" />
        <div className="stack-sm" style={{ marginTop: 16 }}>
          {liveJoinable ? (
            <Button
              onClick={() => (liveConsentRequired ? setPhase({ kind: 'consent', summary, next: '/live' }) : navigate('/live', { replace: true }))}
              data-testid="door-join-live"
              data-consent-first={liveConsentRequired ? 'true' : 'false'}
            >
              {t('sv.live.card.action')}
            </Button>
          ) : (
            <Button onClick={continueFromDoor} data-testid="door-continue">
              {t('sv.door.cta')}
            </Button>
          )}
        </div>
      </div>
    </Shell>
  );
}
